import { useEffect, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Share2, Copy, Check, Heart } from "lucide-react";
import { toast } from "sonner";
import { donationUrlFor } from "@/lib/links";

/* Canvas size — 1080×1080 is the safest square format for Instagram /
   Facebook / LinkedIn cards. */
const W = 1080;
const H = 1080;

const COLORS = {
  bg: "#FAF6EE",
  navy: "#122036",
  blue: "#3DA9DC",
  green: "#22C55E",
  coral: "#F4A6A6",
  yellow: "#F4B91C",
  muted: "#78716C",
};

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

async function renderCard(canvas, family) {
  const ctx = canvas.getContext("2d");
  canvas.width = W;
  canvas.height = H;

  // Background — cream with subtle radial accents
  ctx.fillStyle = COLORS.bg;
  ctx.fillRect(0, 0, W, H);

  const g1 = ctx.createRadialGradient(W * 0.85, -40, 40, W * 0.85, -40, 700);
  g1.addColorStop(0, "rgba(61,169,220,0.18)");
  g1.addColorStop(1, "rgba(61,169,220,0)");
  ctx.fillStyle = g1;
  ctx.fillRect(0, 0, W, H);

  const g2 = ctx.createRadialGradient(-60, H + 60, 40, -60, H + 60, 700);
  g2.addColorStop(0, "rgba(244,166,166,0.20)");
  g2.addColorStop(1, "rgba(244,166,166,0)");
  ctx.fillStyle = g2;
  ctx.fillRect(0, 0, W, H);

  // Top "Powered by" lockup
  try {
    const logo = await loadImage("/bookshelf-logo.png");
    const lw = 110, lh = 110;
    ctx.drawImage(logo, 80, 80, lw, lh);
  } catch (_e) {
    // fall through if logo fails to load
  }
  ctx.fillStyle = COLORS.navy;
  ctx.font = "600 36px 'Lora', Georgia, serif";
  ctx.textBaseline = "alphabetic";
  ctx.fillText("The Bookshelf Project, Inc.", 220, 130);
  ctx.fillStyle = COLORS.blue;
  ctx.font = "600 22px 'Figtree', system-ui, sans-serif";
  const tagline = "CREATING IN-HOME LIBRARIES FOR CHILDREN";
  ctx.fillText(tagline, 220, 168);

  // Soft divider
  ctx.strokeStyle = "rgba(18,32,54,0.10)";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(80, 230);
  ctx.lineTo(W - 80, 230);
  ctx.stroke();

  // Big eyebrow chip
  ctx.fillStyle = COLORS.green;
  const eyebrow = "JUST INSTALLED";
  ctx.font = "700 22px 'Figtree', sans-serif";
  const ew = ctx.measureText(eyebrow).width;
  const chipPadX = 26;
  const chipH = 50;
  const chipY = 290;
  ctx.beginPath();
  const radius = 25;
  ctx.fillStyle = "rgba(34,197,94,0.14)";
  ctx.roundRect(80, chipY, ew + chipPadX * 2, chipH, radius);
  ctx.fill();
  ctx.fillStyle = "#16A34A";
  ctx.fillText(eyebrow, 80 + chipPadX, chipY + 33);

  // Headline — first name + neighborhood
  ctx.fillStyle = COLORS.navy;
  ctx.font = "700 96px 'Lora', Georgia, serif";
  ctx.textBaseline = "top";
  const headLine1 = `${family.first_name} ${family.last_initial}.`;
  ctx.fillText(headLine1, 80, 370);

  ctx.font = "400 52px 'Lora', Georgia, serif";
  ctx.fillStyle = "#44403C";
  const headLine2 = `${family.neighborhood || family.jurisdiction}${family.ward ? " · Ward " + family.ward : ""}`;
  ctx.fillText(headLine2, 80, 490);

  // Quote (if present) — italic, indented
  if (family.family_quote) {
    ctx.font = "italic 400 34px 'Lora', Georgia, serif";
    ctx.fillStyle = "#1C1917";
    const quote = `"${family.family_quote}"`;
    const maxWidth = W - 160;
    wrapText(ctx, quote, 80, 580, maxWidth, 44);
  }

  // Bottom stat row
  const childrenLabel = `${family.num_children} ${family.num_children === 1 ? "child" : "children"} with a home library`;
  ctx.fillStyle = COLORS.navy;
  ctx.font = "700 42px 'Figtree', sans-serif";
  ctx.textBaseline = "top";
  ctx.fillText(childrenLabel, 80, H - 280);

  // Number callout
  ctx.fillStyle = COLORS.coral;
  ctx.font = "800 200px 'Lora', Georgia, serif";
  ctx.textBaseline = "alphabetic";
  ctx.fillText(String(family.num_children), W - 240, H - 110);

  // Footer "Powered by"
  ctx.fillStyle = COLORS.muted;
  ctx.font = "600 22px 'Figtree', sans-serif";
  ctx.textBaseline = "alphabetic";
  ctx.fillText("Powered by The Bookshelf Project, Inc. · thebookshelfproject.org", 80, H - 60);
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = text.split(" ");
  let line = "";
  let yy = y;
  for (let n = 0; n < words.length; n++) {
    const testLine = line + words[n] + " ";
    const m = ctx.measureText(testLine);
    if (m.width > maxWidth && n > 0) {
      ctx.fillText(line, x, yy);
      line = words[n] + " ";
      yy += lineHeight;
      if (yy > y + lineHeight * 5) {
        ctx.fillText(line.trim() + "…", x, yy);
        return;
      }
    } else {
      line = testLine;
    }
  }
  ctx.fillText(line, x, yy);
}

export default function ShareCardModal({ family, open, onOpenChange }) {
  const canvasRef = useRef(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || !family) return undefined;
    // Wait one frame so Radix DialogContent has mounted the canvas.
    let cancelled = false;
    const id = window.requestAnimationFrame(() => {
      if (cancelled) return;
      if (!canvasRef.current) {
        // eslint-disable-next-line no-console
        console.warn("ShareCardModal: canvas ref not ready");
        return;
      }
      renderCard(canvasRef.current, family).catch((err) => {
        // eslint-disable-next-line no-console
        console.error("ShareCardModal renderCard error", err);
        toast.error("Couldn't render the share card.");
      });
    });
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(id);
    };
  }, [open, family]);

  if (!family) return null;

  const filename = `bookshelf-${(family.first_name || "story").toLowerCase()}-${family.id.slice(-6)}.png`;

  const onDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Share card downloaded");
  };

  const onCopy = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    try {
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
      if (!blob) throw new Error("blob failed");
      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast.success("Image copied — paste it into any app");
    } catch (_e) {
      toast.message("Copy not supported here. Use Download instead.");
    }
  };

  const onShare = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
    if (!blob) return;
    const file = new File([blob], filename, { type: "image/png" });
    const shareData = {
      files: [file],
      title: "The Bookshelf Project — new home library",
      text: `A new home library was just installed in ${family.neighborhood || family.jurisdiction}. Help us deliver more 📚`,
    };
    if (navigator.canShare && navigator.canShare(shareData)) {
      try { await navigator.share(shareData); } catch (_e) { /* user cancelled */ }
    } else {
      onDownload();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white" data-testid="share-card-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#122036]">Share this story</DialogTitle>
          <DialogDescription>
            A 1080×1080 social-ready card. No private info is included — just the
            first name, neighborhood, and number of children reached.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-2xl overflow-hidden border border-stone-200 shadow-sm bg-stone-50 aspect-square w-full max-h-[60vh] mx-auto">
          <canvas
            ref={canvasRef}
            className="w-full h-full block"
            data-testid="share-card-canvas"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2 justify-end pt-2">
          <Button
            variant="outline"
            onClick={onCopy}
            data-testid="share-card-copy-btn"
            className="rounded-full border-stone-200"
          >
            {copied ? <Check size={14} className="mr-1.5" /> : <Copy size={14} className="mr-1.5" />}
            {copied ? "Copied" : "Copy image"}
          </Button>
          <Button
            variant="outline"
            onClick={onShare}
            data-testid="share-card-share-btn"
            className="rounded-full border-stone-200"
          >
            <Share2 size={14} className="mr-1.5" /> Share…
          </Button>
          <Button
            variant="outline"
            onClick={onDownload}
            data-testid="share-card-download-btn"
            className="rounded-full border-stone-200"
          >
            <Download size={14} className="mr-1.5" /> Download PNG
          </Button>
          <a
            href={donationUrlFor("share-card")}
            target="_blank"
            rel="noreferrer"
            data-testid="share-card-donate-btn"
          >
            <Button className="bg-[#3DA9DC] hover:bg-[#2C8FBE] text-white rounded-full shadow-sm">
              <Heart size={14} className="mr-1.5" />
              Sponsor a Library
            </Button>
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
}
